/**
 * Maps a sign-in failure to a user-facing message.
 *
 * Wrong-password and unknown-user errors collapse to one message so the screen
 * can't be used to discover which emails are registered. Only connectivity and
 * rate-limit failures — which reveal nothing about the account — get a distinct
 * message; a forced password reset is surfaced as a safe, known state. Handles
 * both Cognito/Amplify errors (err.code) and axios errors (err.response).
 *
 * @param {Error} err Error thrown by Auth.signIn or a post-auth API call.
 * @returns {string} Message to display on the login screen.
 */
export const getLoginErrorMessage = (err) => {
	const isOffline =
		typeof navigator !== "undefined" && navigator.onLine === false;
	const code = (err && (err.code || err.name)) || "";
	const message = (err && err.message) || "";
	const noResponse = !!(err && err.request && !err.response); // axios: request sent, nothing came back

	// Connectivity — safe to distinguish, reveals nothing about the account.
	if (
		isOffline ||
		code === "NetworkError" ||
		/network error/i.test(message) ||
		noResponse
	) {
		return "We couldn't connect. Please check your internet connection and try again.";
	}

	// Rate limiting / lockout — about request volume, not account existence.
	// Cognito returns NotAuthorizedException with "Password attempts exceeded." on lockout.
	if (
		code === "LimitExceededException" ||
		code === "TooManyRequestsException" ||
		/attempts exceeded/i.test(message)
	) {
		return "Too many sign-in attempts. Please wait a few minutes and try again.";
	}

	// An admin has forced a password reset on this account.
	if (code === "PasswordResetRequiredException") {
		return 'Your password needs to be reset. Use the "Forgot password" link to set a new one.';
	}

	// Invalid credentials — wrong password and unknown user collapse together (anti-enumeration).
	if (code === "NotAuthorizedException" || code === "UserNotFoundException") {
		return "The email or password you entered is incorrect.";
	}

	// Everything else, including UserNotConfirmedException (kept generic by design).
	return "Something went wrong while signing in. Please try again.";
};
